/*
 * Copyright (c) Nexign, JSC, 2022-2024.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Sergey Mysov
 * @author Viacheslav Smalenski
 * @version 1.29.0
 * @since 1.19.0
 */

import { useMemo } from 'react';

import { getIsFastActionPage } from '@/main/components/Table/utils/tableUtils';
import CheckboxHeader from '@/main/modules/table/components/Basket/Checkbox/CheckboxHeader';
import { ICommonTranslation } from '@modules/internationalization/types';
import IPageSubscriberMobileTranslation from '@modules/internationalization/types/pages/subscriberMobile';
import {
    TableCell,
    TableCellBalance,
    TableCellDate,
    TableCellLabel,
    TableCellLink,
    TableCellText,
    TableColumnCheckbox,
    TableFilterLabels,
    TableFilterMultiSelect,
    TableFilterRange,
    TableFilterRangeDatePicker,
    TableFilterSelect,
    TableHeader,
    TTableColumnConfig,
    useTableCellAccount,
    useTableCellCampaign,
    useTableMsisdnCell,
} from '@modules/table';

import { ECampaignsType } from '../../PageCampaign';
import { EFormatData } from '../../PageDashboard/types/widgetTypes';
import { filterMsisdnInputHandler } from '../helpers/filterMsisdnInputHandler';
import { filterMsisdnSearchInputTooltipMsg } from '../helpers/filterMsisdnSearchInputTooltipMsg';
import { filterMsisdnSearchInputType } from '../helpers/filterMsisdnSearchInputType';
import { ISubscriberTable } from '../types';
import TableSubscriberCellPersData from './TableSubscriberCellPersData';
import TableSubscriberFilter from './TableSubscriberFilter';

export const TableSubscriberColumns = (
    { table, tableFilter }: IPageSubscriberMobileTranslation,
    commonTranslation: ICommonTranslation,
    isMultiSearchEnabled: boolean,
    msisdnFiltersMaxSize?: number,
    disableCheckedAll?: boolean,
    disableCheckedAllMessage?: string,
): TTableColumnConfig<ISubscriberTable> => {
    const useAccountCell = ({ account }: ISubscriberTable) => useTableCellAccount(account.id, account.number);

    const useAssistantCampaignCell = ({ assistantCampaignsCount, msisdn }: ISubscriberTable) =>
        useTableCellCampaign(msisdn, assistantCampaignsCount, ECampaignsType.subscriber);
    const useCoordinatorCampaignCell = ({ coordinatorCampaignsCount, msisdn }: ISubscriberTable) =>
        useTableCellCampaign(msisdn, coordinatorCampaignsCount, ECampaignsType.subscriber);

    const useMsisdnCell = ({ msisdn, id }: ISubscriberTable) => useTableMsisdnCell(id, msisdn);
    const filterMsisdnInputHandlerCallback = filterMsisdnInputHandler(isMultiSearchEnabled, msisdnFiltersMaxSize);
    const filterMsisdnSearchInputTypeCallback = filterMsisdnSearchInputType(msisdnFiltersMaxSize);
    const filterMsisdnSearchInputTooltipMsgCallback = filterMsisdnSearchInputTooltipMsg(
        msisdnFiltersMaxSize,
        tableFilter,
    );

    const isFastActionPage = getIsFastActionPage();

    const msisdnCell = useMemo(() => 
        isFastActionPage 
            ? TableCellText<ISubscriberTable>(({ msisdn }) => msisdn)
            : TableCellLink<ISubscriberTable>(useMsisdnCell),
        [isFastActionPage]);

    const accountCell = useMemo(() => 
        isFastActionPage
            ? TableCellText<ISubscriberTable>(({ account }) => account.number)
            : TableCellLink<ISubscriberTable>(useAccountCell)
        ,[isFastActionPage]);

    return {
        /* Checkbox */
        checked: {
            Header: <CheckboxHeader disabled={disableCheckedAll} disabledMessage={disableCheckedAllMessage}/>,
            ...TableColumnCheckbox<ISubscriberTable>((row) => ({
                checked: isFastActionPage ? !!row.fastActionChecked : row.checked,
                item: { id: row.id, label: row.msisdn },
            })),
        },             
        /* Уровень доступа */
        accessName: {
            accessor: 'accessName',
            Header: (
                <TableHeader label={table.accessName.header ?? ''} name="accessName">
                    <TableSubscriberFilter>
                        <TableFilterMultiSelect name="accessName" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellText<ISubscriberTable>(({ accessName }) => accessName),
        },
        /* Лицевой счет */
        account: {
            accessor: 'account',
            Header: (
                <TableHeader label={table.account.header} name="account">
                    <TableSubscriberFilter>
                        <TableFilterMultiSelect name="account" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...accountCell,
        },
        /* Название */
        accountName: {
            accessor: 'accountName',
            Header: (
                <TableHeader label={table.accountName.header ?? ''} name="accountName">
                    <TableSubscriberFilter>
                        <TableFilterMultiSelect name="accountName" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellText<ISubscriberTable>(({ accountName }) => accountName),
        },
        /* Предложения */
        assistantCampaignsCount: {
            accessor: 'assistantCampaignsCount',
            Header: (
                <TableHeader label={table.assistantCampaignsCount.header} name="assistantCampaignsCount">
                    <TableSubscriberFilter>
                        <TableFilterSelect name="assistantCampaignsCount" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellLink<ISubscriberTable>(useAssistantCampaignCell),
            align: 'right',
        },
        coordinatorCampaignsCount: {
            accessor: 'coordinatorCampaignsCount',
            Header: (
                <TableHeader label={table.assistantCampaignsCount.header} name="coordinatorCampaignsCount">
                    <TableSubscriberFilter>
                        <TableFilterSelect name="coordinatorCampaignsCount" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellLink<ISubscriberTable>(useCoordinatorCampaignCell),
            align: 'right',
        },
        /* баланс | условный баланс */
        balance: {
            accessor: 'balance',
            Header: (
                <TableHeader label={table.balance.header} name="balance">
                    <TableSubscriberFilter>
                        <TableFilterRange isCurrency name="balance" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellBalance<ISubscriberTable>(({ balance }) => ({
                date: balance?.date,
                value: balance?.value,
                currency: '',
            })),
        },
        /* Дополнительный городской номер */
        cityNumber: {
            accessor: 'cityNumber',
            Header: (
                <TableHeader label={table.cityNumber.header} name="cityNumber">
                    <TableSubscriberFilter>
                        <TableFilterMultiSelect name="cityNumber" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellText<ISubscriberTable>(({ cityNumber }) => cityNumber),
        },
        /* Договор */
        contractNumber: {
            accessor: 'contractNumber',
            Header: (
                <TableHeader label={table.contractNumber.header} name="contractNumber">
                    <TableSubscriberFilter>
                        <TableFilterMultiSelect name="contractNumber" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellText<ISubscriberTable>(({ contractNumber }) => contractNumber),
        },
        /* Правила разделения расходов */
        dcRules: {
            accessor: 'dcRules',
            Header: (
                <TableHeader label={table.dcRules.header} name="dcRules">
                    <TableSubscriberFilter>
                        <TableFilterMultiSelect name="dcRules" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellText<ISubscriberTable>(({ dcRules }) => dcRules),
        },
        /* ICC */
        icc: {
            accessor: 'icc',
            Header: (
                <TableHeader label={table.icc.header} name="icc">
                    <TableSubscriberFilter>
                        <TableFilterMultiSelect name="icc" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellText<ISubscriberTable>(({ icc }) => icc),
        },
        /* ИНН */
        inn: {
            accessor: 'inn',
            Header: (
                <TableHeader label={table.inn.header} name="inn">
                    <TableSubscriberFilter>
                        <TableFilterMultiSelect name="inn" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellText<ISubscriberTable>(({ inn }) => inn),
        },
        /* КПП */
        kpp: {
            accessor: 'kpp',
            Header: (
                <TableHeader label={table.kpp.header} name="kpp">
                    <TableSubscriberFilter>
                        <TableFilterMultiSelect name="kpp" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellText<ISubscriberTable>(({ kpp }) => kpp),
        },
        /* Метки */
        labels: {
            accessor: 'labels',
            Header: (
                <TableHeader disableSort={true} label={table.labels.header} name="labels">
                    <TableSubscriberFilter>
                        <TableFilterLabels name="labels" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellLabel<ISubscriberTable>(({ labels }) => labels ?? []),
        },
        /* Статус участия */
        loyaltyStatus: {
            accessor: 'loyaltyStatus',
            Header: (
                <TableHeader description={table.loyaltyStatus.description} label={table.loyaltyStatus.header} name="loyaltyStatus">
                    <TableSubscriberFilter>
                        <TableFilterSelect name="loyaltyStatus" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellText<ISubscriberTable>(({ loyaltyStatus }) => loyaltyStatus),
        },
        /* Номер */
        msisdn: {
            accessor: 'msisdn',
            Header: (
                <TableHeader label={table.msisdn.header} name="msisdn">
                    <TableSubscriberFilter>
                        <TableFilterMultiSelect
                            filterInputHandlerCallback={filterMsisdnInputHandlerCallback}
                            name="msisdn"
                            searchInputMaxLength={msisdnFiltersMaxSize}
                            searchInputTooltipMsgCallback={filterMsisdnSearchInputTooltipMsgCallback}
                            searchInputTypeCallback={filterMsisdnSearchInputTypeCallback}
                        />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            sticky: 'left',
            ...msisdnCell,
        },
        /* ФИО */
        name: {
            accessor: 'name',
            Header: (
                <TableHeader label={table.name.header} name="name">
                    <TableSubscriberFilter>
                        <TableFilterMultiSelect name="name" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellText<ISubscriberTable>(({ name }) => name),
        },
        /* Тип нумерации */
        numberType: {
            accessor: 'numberType',
            Header: (
                <TableHeader label={table.numberType.header} name="numberType">
                    <TableSubscriberFilter>
                        <TableFilterMultiSelect name="numberType" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellText<ISubscriberTable>(({ numberType }) => numberType),
        },
        /* Персональные данные*/
        persDataStatusName: {
            accessor: 'persDataStatusName',
            Header: (
                <TableHeader label={table.persDataStatusName.header} name="persDataStatusName">
                    <TableSubscriberFilter>
                        <TableFilterMultiSelect name="persDataStatusName" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            Cell: (props) =>
                TableCell(props)({
                    render: ({ row }) => (
                        <TableSubscriberCellPersData commonTranslation={commonTranslation} row={row} />
                    ),
                }),
        },
        /* Плановая дата блокировки */
        persDataLockDate: {
            accessor: 'persDataLockDate',
            Header: (
                <TableHeader label={table.persDataLockDate.header} name="persDataLockDate">
                    <TableSubscriberFilter>
                        <TableFilterRangeDatePicker name="persDataLockDate" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellDate<ISubscriberTable>(({ persDataLockDate }) => ({
                date: persDataLockDate,
                format: EFormatData.FullDateWithTime,
            })),
        },
        /* Должность */
        position: {
            accessor: 'position',
            Header: (
                <TableHeader label={table.position.header} name="position">
                    <TableSubscriberFilter>
                        <TableFilterMultiSelect name="position" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellText<ISubscriberTable>(({ position }) => position),
        },
        /* Тариф */
        ratePlan: {
            accessor: 'ratePlan',
            Header: (
                <TableHeader label={table.ratePlan.header} name="ratePlan">
                    <TableSubscriberFilter>
                        <TableFilterMultiSelect name="ratePlan" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellText<ISubscriberTable>(({ ratePlan }) => ratePlan),
        },
        /* Статус */
        status: {
            accessor: 'status',
            Header: (
                <TableHeader label={table.status.header} name="status">
                    <TableSubscriberFilter>
                        <TableFilterMultiSelect name="status" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellText<ISubscriberTable>(({ status }) => status),
        },
        /* КПП структурного подразделения */
        subdivisionKpp: {
            accessor: 'subdivisionKpp',
            Header: (
                <TableHeader label={table.subdivisionKpp.header} name="subdivisionKpp">
                    <TableSubscriberFilter>
                        <TableFilterMultiSelect name="subdivisionKpp" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellText<ISubscriberTable>(({ subdivisionKpp }) => subdivisionKpp),
        },
        /* Название структурного подразделения */
        subdivisionName: {
            accessor: 'subdivisionName',
            Header: (
                <TableHeader label={table.subdivisionName.header} name="subdivisionName">
                    <TableSubscriberFilter>
                        <TableFilterMultiSelect name="subdivisionName" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellText<ISubscriberTable>(({ subdivisionName }) => subdivisionName),
        },
        /* Регион */
        zone: {
            accessor: 'zone',
            Header: (
                <TableHeader label={table.zone.header} name="zone">
                    <TableSubscriberFilter>
                        <TableFilterMultiSelect name="zone" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellText<ISubscriberTable>(({ zone }) => zone),
        },
        /* Текущая страна роуминга */
        currCountryName: {
            accessor: 'currCountryName',
            Header: (
                <TableHeader label={table.currCountryName.header} name="currCountryName">
                    <TableSubscriberFilter>
                        <TableFilterMultiSelect name="currCountryName" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellText<ISubscriberTable>(({ currCountryName }) => currCountryName),
        },
        /* Дата регистрации в стране роуминга */
        eventTimeChangeCountry: {
            accessor: 'eventTimeChangeCountry',
            Header: (
                <TableHeader label={table.eventTimeChangeCountry.header} name="eventTimeChangeCountry">
                    <TableSubscriberFilter>
                        <TableFilterRangeDatePicker name="eventTimeChangeCountry" />
                    </TableSubscriberFilter>
                </TableHeader>
            ),
            ...TableCellDate<ISubscriberTable>(({ eventTimeChangeCountry }) => ({
                date: eventTimeChangeCountry,
                format: EFormatData.FullData,
            })),
            rowCount: 2,
        },
    };
};
